import { useRef, useState } from "react";
import { CheckCircle2, XCircle, KeyRound, ShieldCheck, Pencil, Trash2, PlugZap, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useTestServer, useDeleteServer, useResetFingerprint, errorMessage } from "../../hooks/useServers";
import { ScanPanel } from "./ScanPanel";
import { CredentialsCard } from "./CredentialsCard";

export function ServerRow({ projectId, server, onEdit }) {
  const [testResult, setTestResult] = useState(null);
  const [showCredentials, setShowCredentials] = useState(false);
  const testing = useRef(false); // anti doble clic en "Probar conexión"
  const test = useTestServer(projectId);
  const remove = useDeleteServer(projectId);
  const resetFingerprint = useResetFingerprint(projectId);

  const handleTest = async () => {
    if (testing.current) return;
    testing.current = true;
    setTestResult(null);
    try {
      const result = await test.mutateAsync(server.id);
      setTestResult({ ok: result.ok, message: result.message });
    } catch (err) {
      setTestResult({ ok: false, message: errorMessage(err, "No se pudo probar la conexión.") });
    } finally {
      testing.current = false;
    }
  };

  const handleDelete = () => {
    if (remove.isPending) return;
    if (window.confirm(`¿Eliminar el servidor "${server.name}"? Se borra también su contraseña guardada.`)) {
      remove.mutate(server.id, { onError: (err) => window.alert(errorMessage(err, "No se pudo eliminar el servidor.")) });
    }
  };

  const handleResetFingerprint = () => {
    if (resetFingerprint.isPending) return;
    if (
      window.confirm(
        (server.type === "routeros" || server.type === "olt"
          ? "Esto borra la identidad guardada del equipo y levanta el bloqueo; la próxima conexión guardará la nueva. "
          : "Esto borra la huella guardada del servidor; la próxima conexión guardará la nueva. ") +
          "Hazlo solo si sabes que el servidor se reinstaló o cambió de llave. ¿Continuar?",
      )
    ) {
      resetFingerprint.mutate(server.id);
    }
  };

  const shown = testResult || (server.last_test_at_mx ? { ok: server.last_test_ok, message: server.last_test_message } : null);

  return (
    <div data-testid={`server-row-${server.name}`} className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{server.name}</span>
            <Badge variant={server.access === "read_only" ? "secondary" : "default"} className="text-[10px]">
              {server.access === "read_only" ? "Solo lectura" : server.type === "routeros" ? "Lectura + cambios (lo peligroso se confirma en el chat)" : "Lectura + cambios con confirmación"}
            </Badge>
            {(server.type === "routeros" || server.type === "olt") && (
              <Badge variant="outline" className="text-[10px]" data-testid={`server-type-${server.name}`}>
                {server.type === "olt" ? "OLT Huawei (Telnet)" : "MikroTik RouterOS (API)"}
              </Badge>
            )}
            {server.jump_via_name && (
              <Badge variant="outline" className="text-[10px]" data-testid={`server-via-${server.name}`}>
                vía {server.jump_via_name}
              </Badge>
            )}
            {server.db_configured && (
              <Badge variant="outline" className="text-[10px]" data-testid={`server-db-${server.name}`}>
                BD configurada
              </Badge>
            )}
            {server.used_as_jump_by?.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                puerta de salto de {server.used_as_jump_by.join(", ")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono truncate">
            {server.username}@{server.host}:{server.port}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(server)} title="Editar">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleDelete} disabled={remove.isPending} title="Eliminar">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        <span className="inline-flex items-center gap-1">
          <KeyRound className="h-3.5 w-3.5" />
          {server.auth_method === "key" ? "Llave" : "Contraseña"}: ••• guardada
        </span>
        {server.type === "routeros" || server.type === "olt" ? (
          <span className="inline-flex items-center gap-1" data-testid={`server-identity-${server.name}`}>
            <ShieldCheck className={`h-3.5 w-3.5 ${server.routeros_identity ? "text-emerald-500" : ""}`} />
            {server.routeros_blocked_until_mx
              ? `Bloqueado hasta ${server.routeros_blocked_until_mx} (credenciales rechazadas)`
              : server.routeros_identity
                ? `Equipo «${server.routeros_identity}»`
                : "Identidad del equipo: se guarda en la primera conexión"}
            <button type="button" className="underline hover:text-foreground" onClick={handleResetFingerprint}>
              restablecer
            </button>
          </span>
        ) : server.host_fingerprint ? (
          <span className="inline-flex items-center gap-1" title={server.host_fingerprint}>
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Huella verificada
            <button type="button" className="underline hover:text-foreground" onClick={handleResetFingerprint}>
              restablecer
            </button>
          </span>
        ) : (
          <span>Huella: se guarda en la primera conexión</span>
        )}
      </div>

      {server.notes && <p className="text-xs text-muted-foreground">{server.notes}</p>}

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          data-testid={`server-test-${server.name}`}
          size="sm"
          variant="outline"
          onClick={handleTest}
          disabled={test.isPending}
        >
          <PlugZap className="h-3.5 w-3.5 mr-1.5" />
          {test.isPending ? "Probando..." : "Probar conexión"}
        </Button>
        <Button
          data-testid={`server-credentials-${server.name}`}
          size="sm"
          variant="outline"
          onClick={() => setShowCredentials((v) => !v)}
        >
          <Eye className="h-3.5 w-3.5 mr-1.5" />
          Ver credenciales
        </Button>
        {shown && (
          <span className={`inline-flex items-start gap-1 text-xs ${shown.ok ? "text-emerald-500" : "text-red-500"}`}>
            {shown.ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
            <span>
              {shown.message}
              {!testResult && server.last_test_at_mx ? ` — ${server.last_test_at_mx}` : ""}
            </span>
          </span>
        )}
      </div>

      {showCredentials && <CredentialsCard projectId={projectId} serverId={server.id} onHide={() => setShowCredentials(false)} />}

      {server.type !== "routeros" && server.type !== "olt" && <ScanPanel projectId={projectId} server={server} />}
    </div>
  );
}
